// REF-6708 — Session Replay never records while the PAGE URL carries a secret (D-B1, option 2). (A URL inside the
// DOM — an `href` / `src` attribute on a safe page — is out of scope: U-1, docs/systems/sentry.md.)
//
// Why: rrweb puts the WHOLE `location.href` into the Meta event of every full snapshot (the start, each
// checkout, each restart), and no Sentry hook reaches it — `beforeAddRecordingEvent` sees custom events only.
// So on a token landing (`/reset-password/<t>`, `/auth/magic/<t>`, a redacted query, any fragment, …) the
// recording itself would carry the token. The only fix that keeps the sampling rates is to not record while
// the URL is unsafe. "Unsafe" is exactly what the shared B1 policy says: `sanitizeUrl(href) !== href`.
//
// How (SDK 10.43, public API only):
//   - The Replay integration is NOT passed to `Sentry.init`. Replay samples and starts rrweb the moment it is
//     set up, before React mounts, so registering it at init would snapshot a token landing at once. The gate
//     registers it with `Sentry.addIntegration` the first time the URL is safe; the SDK then draws its own
//     sampling decision with the client's unchanged rates.
//   - Entering an unsafe URL: remember `getRecordingMode()` ("session" | "buffer" | undefined) and `stop()` —
//     before React renders the page and before Sentry's own history handler sees the navigation (that handler
//     can resume an idle-paused recording or refresh an expired session; both bail out on a stopped Replay).
//     The `pushState` / `replaceState` wrapper is outermost and stops BEFORE the call when the target is unsafe;
//     the `popstate` / `hashchange` listeners are registered before `Sentry.init` (`prepareReplayGate`).
//   - After every such stop the gate looks again once the SDK's own continuations have run (a session refresh or
//     an error-replay promotion already in flight can restart Replay without looking at the URL) and stops it.
//   - Leaving it: once every pending stop has finished, `start()` for a session recording, `startBuffering()`
//     for a buffer one, nothing for an unsampled one. Never `start()` blindly: it would record every user in full.
//   - A generation counter drops a resume that a later navigation has overtaken, and a resume never starts
//     Replay a second time.
//   - `reconcileReplayGate()` is also called from a router effect, as a backstop for any URL change the
//     listeners above miss. It is idempotent, so StrictMode's double effects are harmless.
//
// Accepted effects (user decision, documented in docs/systems/sentry.md): the buffered safe lead-up is
// discarded on entering a secret page (`stop()` destroys the buffer); a session replay is split into two
// replay ids around a secret page; a secret landing draws its sampling on the first safe page (a still-valid
// sticky replay session from a reload continues instead).
import * as Sentry from "@sentry/react";
import { sanitizeUrl } from "utils/privacy/telemetryUrl";

type RecordingMode = "session" | "buffer";

export interface GatedReplay {
  name: string;
  getRecordingMode(): RecordingMode | undefined;
  start(): void;
  startBuffering(): void;
  stop(): Promise<void>;
}

/** True when the shared B1 policy would change this URL — a token path, a redacted query, any fragment. */
export const isUnsafeUrl = (href: string): boolean => sanitizeUrl(href) !== href;

let replay: GatedReplay | null = null;
let registered = false;
// Whether the gate has stopped Replay because the URL is unsafe (and owes it a resume).
let stoppedForUrl = false;
// The recording mode to restore on the next safe URL.
let resumeMode: RecordingMode | undefined;
let stopping: Promise<void> = Promise.resolve();
let generation = 0;
let installed = false;
let listening = false;

const currentHref = (): string => window.location.href;

// The SDK sets integrations up only on an enabled client (a DSN and `enabled !== false`) — off app.refabric.com
// Replay never ran. `addIntegration` skips that check, so the gate makes it itself.
const clientEnabled = (): boolean => {
  const client = Sentry.getClient();

  return Boolean(client && client.getOptions().enabled !== false && client.getDsn());
};

const resume = async (mode: RecordingMode | undefined, ownGeneration: number): Promise<void> => {
  // A `stop()` still in flight would destroy the buffer and session a resume creates.
  await stopping;
  if (ownGeneration !== generation || !replay || isUnsafeUrl(currentHref())) return;
  // Already recording (an earlier resume won, or the SDK restarted itself): never a second start.
  if (replay.getRecordingMode()) return;
  if (mode === "session") replay.start();
  else if (mode === "buffer") replay.startBuffering();
};

const stopReplay = (): void => {
  if (!replay) return;
  // Unsampled, or a resume not yet run: keep the mode we already owe.
  const mode = replay.getRecordingMode();
  if (mode) resumeMode = mode;
  // Chained, never replaced: a second `stop()` on an already-stopped Replay resolves at once, while the
  // first may still be flushing — and a resume must wait for EVERY stop, or the older one's continuation
  // destroys the buffer and session the resume just created.
  const previous = stopping;
  const current = replay.stop().catch(() => undefined);
  stopping = Promise.all([previous, current]).then(() => undefined);
};

// How many microtask turns to keep looking for an SDK restart after a stop (the session refresh restarts after
// a handful: `await stop()` with no flush, then `initializeSampling()`).
const RESTART_WATCH_MICROTASKS = 20;

/**
 * The SDK can restart Replay on its own after the gate stopped it: a session refresh already in flight
 * (`_refreshSession`: `await stop()` then `initializeSampling()`, started by a click or key press just before
 * the navigation) — it does not look at the URL. Its restart runs on MICROTASKS right after the navigation, so
 * the gate looks again on the microtask queue too: the restart is caught in the same turn, before any redirect
 * back to a safe page could adopt it and before any time passes — the restart is a brand-new session, younger
 * than `minReplayDuration`, so the stop sends nothing. A timer and the end of the pending stops are checked as
 * well, as a backstop. (An error replay's promotion can restart rrweb itself but not Replay: it stays disabled
 * and records nothing — see the residuals in docs/systems/sentry.md.)
 */
const watchForRestart = (ownGeneration: number): void => {
  const check = () => {
    if (ownGeneration !== generation || !stoppedForUrl || !replay || !isUnsafeUrl(currentHref())) return;
    if (replay.getRecordingMode()) stopReplay();
  };
  void (async () => {
    for (let turn = 0; turn < RESTART_WATCH_MICROTASKS; turn += 1) {
      await Promise.resolve();
      check();
    }
  })();
  setTimeout(check, 0);
  void stopping.then(() => setTimeout(check, 0));
};

const enterUnsafe = (): void => {
  if (stoppedForUrl) return;
  stoppedForUrl = true;
  generation += 1;
  stopReplay();
  watchForRestart(generation);
};

/**
 * Bring Replay in line with the CURRENT URL. Synchronous on the stop side; safe to call any number of times.
 */
export const reconcileReplayGate = (): void => {
  if (!replay) return;
  const unsafe = isUnsafeUrl(currentHref());

  if (!registered) {
    // Not even sampled yet: wait for the first safe URL (and an enabled client).
    if (unsafe || !clientEnabled()) return;
    registered = true;
    Sentry.addIntegration(replay as never);

    return;
  }

  if (unsafe) {
    enterUnsafe();

    return;
  }

  if (!stoppedForUrl) return;
  stoppedForUrl = false;
  generation += 1;
  void resume(resumeMode, generation);
};

// The URL a `pushState` / `replaceState` call is about to set, resolved like the browser does; undefined when the
// call keeps the URL.
const targetOf = (args: unknown[]): string | undefined => {
  const url = args[2];
  if (url === undefined || url === null) return undefined;
  try {
    return new URL(String(url), currentHref()).href;
  } catch {
    return undefined;
  }
};

/**
 * Wraps a History API method. Installed AFTER `Sentry.init`, so it is the OUTERMOST wrapper: Sentry's own
 * history handler runs inside it. When the target URL is unsafe, Replay is stopped BEFORE that handler sees the
 * navigation — which matters, because the handler calls `triggerUserActivity()`, and that can resume an idle-
 * paused recording or refresh an expired session, taking a full snapshot. Both bail out on a stopped Replay.
 * After the call, the real URL is checked (that is where a resume happens).
 */
const gated = <T extends (...args: never[]) => unknown>(native: T): T =>
  function (this: History, ...args: Parameters<T>) {
    const target = targetOf(args);
    if (replay && registered && target !== undefined && isUnsafeUrl(target)) enterUnsafe();
    try {
      return native.apply(this, args);
    } finally {
      // Also when the call throws (a cross-origin URL): the URL did not change, and a stop made for the target
      // must not leave Replay off on the page the user is still on.
      reconcileReplayGate();
    }
  } as unknown as T;

// User activity Replay reacts to (`triggerUserActivity()`: resume an idle-paused recording, refresh an expired
// session — each takes a full snapshot of the CURRENT URL).
const ACTIVITY_EVENTS = ["click", "keydown", "pointerdown", "input"] as const;

/**
 * Register the gate's listeners. Call BEFORE `Sentry.init`, so they run before Sentry's own:
 *  - `popstate` / `hashchange`: Sentry's `popstate` listener calls `triggerUserActivity()` on a URL that has
 *    ALREADY changed. Listeners on one target run in registration order.
 *  - capture-phase `click` / `keydown` / `pointerdown` / `input` on `window`: a URL can change synchronously
 *    (`location.hash = …`) while its `hashchange` / `popstate` notification is still a queued task. User
 *    activity in that window reaches Sentry's handlers (on `document`, and its wrapped element listeners)
 *    before that notification — and they resume or refresh the recording on the new URL. A capture listener
 *    on `window` runs before every one of them, so the gate re-checks the live URL first.
 * `installReplayGate` calls it too, so it is harmless to skip — only the order is lost.
 */
export const prepareReplayGate = (): void => {
  if (listening) return;
  listening = true;
  window.addEventListener("popstate", reconcileReplayGate);
  window.addEventListener("hashchange", reconcileReplayGate);
  ACTIVITY_EVENTS.forEach((type) => window.addEventListener(type, reconcileReplayGate, true));
};

/**
 * Take over the Replay integration (instead of passing it to `Sentry.init`) and register it when, and only
 * while, the URL is safe. Call once, right after `Sentry.init`.
 */
export const installReplayGate = (integration: GatedReplay): void => {
  if (installed) return;
  installed = true;
  replay = integration;
  prepareReplayGate();

  // The prototype too: `History.prototype.pushState.call(history, …)`, or a reference to it captured before this
  // ran, would otherwise change the URL with no wrapper seeing it.
  History.prototype.pushState = gated(History.prototype.pushState);
  History.prototype.replaceState = gated(History.prototype.replaceState);
  window.history.pushState = gated(window.history.pushState);
  window.history.replaceState = gated(window.history.replaceState);

  reconcileReplayGate();
};

/** The gated Replay integration (the one `instrument.ts` built), for tests reading the shipped options. */
export const getGatedReplay = (): GatedReplay | null => replay;

/**
 * TEST ONLY — make the gate's History API wrapper the OUTERMOST one again, as it is in the app (the test harness
 * starts its capturing client after `instrument.ts`, so Sentry wraps the History API after the gate did).
 */
export const wrapHistoryOutermostForTests = (): void => {
  window.history.pushState = gated(window.history.pushState);
  window.history.replaceState = gated(window.history.replaceState);
};

/**
 * TEST ONLY — undo `installReplayGate` (drop the listeners, stop Replay, forget all state), so each test can boot
 * a fresh SDK in its own module registry. Never called by the app. The History API wrappers stay in place but
 * become pass-through (`replay` is null): unwrapping them would also remove Sentry's own history wrapper, which
 * the SDK installs only once per page.
 */
export const uninstallReplayGateForTests = async (): Promise<void> => {
  window.removeEventListener("popstate", reconcileReplayGate);
  window.removeEventListener("hashchange", reconcileReplayGate);
  ACTIVITY_EVENTS.forEach((type) => window.removeEventListener(type, reconcileReplayGate, true));
  await stopping;
  await replay?.stop();
  replay = null;
  registered = false;
  stoppedForUrl = false;
  resumeMode = undefined;
  stopping = Promise.resolve();
  generation = 0;
  installed = false;
  listening = false;
};
